// ─────────────────────────────────────────────────────────────────────────────
// FILE: src/pages/hr/LeaveTypesPage.jsx
// ─────────────────────────────────────────────────────────────────────────────
import React, { useState, useEffect } from "react";
import { Plus, Edit2, Trash2, ShieldAlert, X, Eye, CheckCircle2, XCircle } from "lucide-react";
import { toast } from "react-hot-toast"; 
import SummaryApi from "../../common/SummaryApi";
import api from "../../common/apiClient";
import PageHeader from "../ui/PageHeader";
import ModernTable from "../ui/ModernTable";
import SelectDropdown from "../../customStyles/SelectDropdown";

const LeaveTypesPage = () => {
  // --- State Management ---
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isViewOpen, setIsViewOpen] = useState(false); // Added view state for details drawer
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);

  // Edit / Delete / View tracking state
  const [selectedLeaveType, setSelectedLeaveType] = useState(null);
  const [deleteError, setDeleteError] = useState(null);

  // --- Form State (With all Schema fields mapped) ---
  const [formData, setFormData] = useState({
    name: "",
    code: "",
    color: "#6366f1",
    isPaid: true,
    maxDaysPerYear: 12,
    maxConsecutiveDays: 0,
    carryForward: false, 
    maxCarryForward: 0,  
    requiresApproval: true,
    minNoticeDays: 0,
    allowHalfDay: true,
    applicableGender: "all",
    availableDuringProbation: true,
    requiresAttachment: false,
    attachmentThresholdDays: 2,
    isActive: true, 
  });

  const [formErrors, setFormErrors] = useState({});

  const genderOptions = [
    { label: "All Employees", id: "all" },
    { label: "Male Only", id: "male" },
    { label: "Female Only", id: "female" },
  ];

  // ── FETCH ALL LEAVE TYPES ───────────────────────────────────────────────────
  const fetchLeaveTypes = async () => {
    setIsLoading(true);
    try {
      const url = SummaryApi.getAllLeaveTypes?.url || "/api/hr/leave-types/view-all";
      const method = SummaryApi.getAllLeaveTypes?.method || "get";

      const res = await api({ url, method });

      if (res.data.success) {
        setLeaveTypes(res.data.data || []);
      } else {
        toast.error(res.data.message || "Failed to load leave types.");
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Error loading leave configurations.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchLeaveTypes();
  }, []);

  // --- Form Helper Handlers ---
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
    if (formErrors[name]) {
      setFormErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleSelectChange = (field, val) => {
    setFormData((prev) => ({ ...prev, [field]: val }));
    if (formErrors[field]) {
      setFormErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const openFormDrawer = (type = null) => {
    if (type) {
      setSelectedLeaveType(type);
      setFormData({ ...type });
    } else {
      setSelectedLeaveType(null);
      setFormData({
        name: "",
        code: "",
        color: "#6366f1",
        isPaid: true,
        maxDaysPerYear: 12,
        maxConsecutiveDays: 0,
        carryForward: false,
        maxCarryForward: 0,
        requiresApproval: true,
        minNoticeDays: 0,
        allowHalfDay: true,
        applicableGender: "all",
        availableDuringProbation: true,
        requiresAttachment: false,
        attachmentThresholdDays: 2,
        isActive: true,
      });
    }
    setFormErrors({});
    setIsFormOpen(true);
  };

  const closeFormDrawer = () => {
    setIsFormOpen(false);
    setSelectedLeaveType(null);
  };

  // --- View Drawer Handlers ---
  const openViewDrawer = (type) => {
    setSelectedLeaveType(type);
    setIsViewOpen(true);
  };

  const closeViewDrawer = () => {
    setIsViewOpen(false);
    setSelectedLeaveType(null);
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.name.trim()) errors.name = "Name is required";
    if (!formData.code.trim()) errors.code = "Code is required";
    if (formData.maxDaysPerYear < 0) errors.maxDaysPerYear = "Cannot be negative";
    if (formData.carryForward && formData.maxCarryForward < 0) {
      errors.maxCarryForward = "Cannot be negative";
    }
    if (formData.requiresAttachment && formData.attachmentThresholdDays < 1) {
      errors.attachmentThresholdDays = "Must be at least 1 day";
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // ── CREATE / UPDATE LEAVE TYPE SUBMIT ───────────────────────────────────────
  const handleFormSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      let url, method;

      if (selectedLeaveType) {
        url = SummaryApi.updateLeaveType?.url(selectedLeaveType._id) || `/api/hr/leave-types/update-one/${selectedLeaveType._id}`;
        method = SummaryApi.updateLeaveType?.method || "patch";
      } else {
        url = SummaryApi.createLeaveType?.url || "/api/hr/leave-types/create";
        method = SummaryApi.createLeaveType?.method || "post";
      }

      const res = await api({
        url,
        method,
        data: formData,
      });

      if (res.data.success) {
        toast.success(res.data.message || `Leave type ${selectedLeaveType ? "updated" : "created"} successfully!`);
        await fetchLeaveTypes();
        closeFormDrawer();
      } else {
        toast.error(res.data.message || "Operation failed.");
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Error submitting leave configuration.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // --- Delete Handler Setup ---
  const triggerDelete = (type) => {
    setSelectedLeaveType(type);
    setIsDeleteOpen(true);
    setDeleteError(null);
  };

  // ── CONFIRM ACTION DELETE ───────────────────────────────────────────────────
  const confirmDelete = async () => {
    setIsLoading(true);
    try {
      const url = SummaryApi.deleteLeaveTypes?.url(selectedLeaveType._id) || `/api/hr/leave-types/delete-one/${selectedLeaveType._id}`;
      const method = SummaryApi.deleteLeaveTypes?.method || "delete";

      const res = await api({ url, method });

      if (res.data.success) {
        toast.success(res.data.message || "Leave type deleted successfully.");
        await fetchLeaveTypes();
        setIsDeleteOpen(false);
        setSelectedLeaveType(null);
      } else {
        toast.error(res.data.message || "Failed to delete leave configuration.");
      }
    } catch (err) {
      setDeleteError({ 
        message: err.response?.data?.message || "This leave type is linked to existing policies and cannot be deleted." 
      });
    } finally {
      setIsLoading(false);
    }
  };

  // --- Table Columns Definition ---
  const columns = [
    {
      key: "code",
      label: "Code",
      render: (row) => (
        <span
          className="px-3 py-2 rounded-md text-xs font-bold uppercase tracking-wider"
          style={{
            backgroundColor: `${row.color}15`,
            color: row.color,
            border: `1px solid ${row.color}30`,
          }}
        >
          {row.code}
        </span>
      ),
    },
    {
      key: "name",
      label: "Leave Name",
      render: (row) => <span className="font-semibold text-gray-800">{row.name}</span>,
    },
    {
      key: "isPaid",
      label: "Type",
      render: (row) => (
        <span className={`px-2 py-1 rounded text-xs font-medium ${row.isPaid ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-700"}`}>
          {row.isPaid ? "Paid Leave" : "Unpaid Leave"}
        </span>
      ),
    },
    {
      key: "maxDaysPerYear",
      label: "Max Days / Year",
      render: (row) => <span className="font-semibold">{row.maxDaysPerYear} Days</span>,
    },
    {
      key: "applicableGender",
      label: "Applicability",
      render: (row) => (
        <span className="capitalize text-gray-600 text-xs font-medium">
          {row.applicableGender === "all" ? "All Genders" : `${row.applicableGender} Only`}
        </span>
      ),
    },
    {
      key: "requiresAttachment",
      label: "Attachment Rule",
      render: (row) => (
        <span className="text-xs text-gray-500 font-medium">
          {row.requiresAttachment ? `Required if ≥ ${row.attachmentThresholdDays} days` : "Not Required"}
        </span>
      ),
    },
    {
      key: "isActive",
      label: "Status",
      render: (row) => (
        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${row.isActive ? "bg-green-50 text-green-700" : "bg-gray-100 text-gray-600"}`}>
          <span className={`h-1.5 w-1.5 rounded-full ${row.isActive ? "bg-green-500" : "bg-gray-400"}`} />
          {row.isActive ? "Active" : "Inactive"}
        </span>
      ),
    },
  ];

  // Added "View Details" to table action list
  const tableActions = [
    {
      label: "View Details",
      icon: <Eye className="w-4 h-4 text-blue-600" />,
      onClick: (row) => openViewDrawer(row),
    },
    {
      label: "Edit Configuration",
      icon: <Edit2 className="w-4 h-4" />,
      onClick: (row) => openFormDrawer(row),
    },
    {
      label: "Delete Leave Type",
      icon: <Trash2 className="w-4 h-4 text-red-600" />,
      onClick: (row) => triggerDelete(row),
    },
  ];

  return (
    <div className="p-4">
      <PageHeader
        title="Leave Types"
        description="Define and manage global configurations for organizational leave balances and rules."
        action={{
          label: "Add Leave Type",
          icon: <Plus className="w-4 h-4" />, 
          onClick: () => openFormDrawer(),
        }}
      />

      <div className="mt-4">
        <ModernTable
          columns={columns}
          data={leaveTypes}
          actions={tableActions}
          selectable={false}
          actionType="dropdown"
          stickyColumns={1}
        />
      </div>

      {/* ─────────────────────────────────────────────────────────────────── */}
      {/* RIGHT-SIDE VIEW DETAILS DRAWER (Read-only View) */}
      {/* ─────────────────────────────────────────────────────────────────── */}
      <div
        className={`fixed inset-0 top-[57px] md:top-[65px] z-[65] overflow-hidden transition-all duration-300 ${
          isViewOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
      >
        <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" onClick={closeViewDrawer} />
        
        <div className="absolute inset-y-0 right-0 max-w-full flex">
          <div
            className={`w-screen max-w-md bg-white shadow-2xl flex flex-col transform transition-transform duration-300 ease-in-out ${
              isViewOpen ? "translate-x-0" : "translate-x-full"
            }`}
          >
            {/* Drawer Header */}
            <div className="px-6 py-5 border-b border-gray-100 flex items-center justify-between bg-slate-50">
              <div>
                <h3 className="text-lg font-bold text-gray-900">
                  Leave Configuration Details
                </h3>
                <p className="text-xs text-gray-400 mt-0.5">Read-only overview of the specific leave regulations.</p>
              </div>
              <button
                type="button"
                onClick={closeViewDrawer}
                className="p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-all"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Read-only Content Body */}
            <div className="flex-1 overflow-y-auto px-6 py-6 space-y-6">
              {/* Profile Card */}
              <div className="bg-slate-50 p-4 rounded-2xl border border-gray-100 space-y-3">
                <div className="flex items-center gap-3">
                  <span 
                    className="px-3 py-1.5 rounded-lg text-sm font-bold uppercase tracking-wider"
                    style={{
                      backgroundColor: `${selectedLeaveType?.color || "#6366f1"}15`,
                      color: selectedLeaveType?.color || "#6366f1",
                      border: `1px solid ${selectedLeaveType?.color || "#6366f1"}30`
                    }}
                  >
                    {selectedLeaveType?.code || "LV"}
                  </span>
                  <div className="text-lg font-bold text-gray-800">{selectedLeaveType?.name}</div>
                </div>

                <div className="pt-2 flex gap-2 flex-wrap">
                  <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded ${selectedLeaveType?.isPaid ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-700"}`}>
                    {selectedLeaveType?.isPaid ? "Paid Leave" : "Unpaid Leave"}
                  </span>
                  <span className="bg-blue-50 text-blue-700 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded">
                    Applicability: {selectedLeaveType?.applicableGender === "all" ? "All Genders" : `${selectedLeaveType?.applicableGender} Only`}
                  </span>
                  <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded ${selectedLeaveType?.isActive ? "bg-green-50 text-green-700" : "bg-gray-100 text-gray-600"}`}>
                    {selectedLeaveType?.isActive ? "Active" : "Inactive"}
                  </span>
                </div>
              </div>

              {/* SECTION: Allocation Grid details */}
              <div className="space-y-4">
                <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider">Allocation & Guidelines</h4>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div className="p-3 bg-white border border-gray-100 rounded-xl shadow-sm">
                    <div className="text-gray-400 text-xs">Max Days Per Year</div>
                    <div className="font-bold text-gray-800 mt-1">{selectedLeaveType?.maxDaysPerYear || 0} Days</div>
                  </div>
                  <div className="p-3 bg-white border border-gray-100 rounded-xl shadow-sm">
                    <div className="text-gray-400 text-xs">Consecutive Days Limit</div>
                    <div className="font-bold text-gray-800 mt-1">
                      {selectedLeaveType?.maxConsecutiveDays === 0 ? "No Limit" : `${selectedLeaveType?.maxConsecutiveDays} Days`}
                    </div>
                  </div>
                </div>
              </div>

              {/* SECTION: Carry Forward guidelines */}
              <div className="space-y-3">
                <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider">Carry Forward Configuration</h4>
                <div className="p-3.5 bg-slate-50/50 rounded-xl border border-gray-100 flex items-center justify-between text-sm">
                  <div>
                    <div className="font-semibold text-gray-800">Carry Forward Status</div>
                    <p className="text-[11px] text-gray-400">Allows carry over to next fiscal year.</p>
                  </div>
                  <span className={`px-2 py-1 rounded text-xs font-semibold ${selectedLeaveType?.carryForward ? "bg-green-50 text-green-700" : "bg-gray-100 text-gray-500"}`}>
                    {selectedLeaveType?.carryForward ? "Enabled" : "Disabled"}
                  </span>
                </div>

                {selectedLeaveType?.carryForward && (
                  <div className="p-3.5 bg-white border border-gray-200 rounded-xl shadow-sm flex items-center justify-between text-sm">
                    <div className="text-gray-500 font-medium">Maximum Carried Over Limit:</div>
                    <div className="font-bold text-gray-800">{selectedLeaveType?.maxCarryForward || 0} Days</div>
                  </div>
                )}
              </div>

              {/* SECTION: Guideline Checklists */}
              <div className="space-y-3">
                <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider">Leave Checklist Guidelines</h4>
                <div className="space-y-2 bg-slate-50/30 p-4 rounded-xl border border-gray-100">
                  <div className="flex items-center justify-between text-xs py-1">
                    <span className="text-gray-600 font-medium">Requires Manager Approval</span>
                    {selectedLeaveType?.requiresApproval ? <CheckCircle2 className="w-4.5 h-4.5 text-green-500 shrink-0" /> : <XCircle className="w-4.5 h-4.5 text-red-400 shrink-0" />}
                  </div>

                  <div className="flex items-center justify-between text-xs py-1 border-t border-gray-100">
                    <span className="text-gray-600 font-medium">Supports Half-Day bookings</span>
                    {selectedLeaveType?.allowHalfDay ? <CheckCircle2 className="w-4.5 h-4.5 text-green-500 shrink-0" /> : <XCircle className="w-4.5 h-4.5 text-red-400 shrink-0" />}
                  </div>

                  <div className="flex items-center justify-between text-xs py-1 border-t border-gray-100">
                    <span className="text-gray-600 font-medium">Available During Probationary period</span>
                    {selectedLeaveType?.availableDuringProbation ? <CheckCircle2 className="w-4.5 h-4.5 text-green-500 shrink-0" /> : <XCircle className="w-4.5 h-4.5 text-red-400 shrink-0" />}
                  </div>
                </div>
              </div>

              {/* SECTION: Notice Period & Document Upload regulations */}
              <div className="space-y-3">
                <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider">Notice & Attachments</h4>
                <div className="p-3 bg-white border border-gray-100 rounded-xl shadow-sm text-sm space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-500 font-medium">Advance Notice Days:</span>
                    <span className="font-bold text-gray-800">
                      {selectedLeaveType?.minNoticeDays === 0 ? "Same-day applications allowed" : `${selectedLeaveType?.minNoticeDays} Day(s) advance`}
                    </span>
                  </div>

                  <div className="flex justify-between items-center pt-2.5 border-t border-gray-100">
                    <span className="text-gray-500 font-medium">Medical / Attachment Required:</span>
                    <span className={`px-2 py-0.5 rounded text-xs font-semibold ${selectedLeaveType?.requiresAttachment ? "bg-red-50 text-red-600" : "bg-gray-50 text-gray-500"}`}>
                      {selectedLeaveType?.requiresAttachment ? `Yes, if ≥ ${selectedLeaveType?.attachmentThresholdDays} days` : "No"}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Drawer Footer */}
            <div className="px-6 py-4 border-t border-gray-100 bg-slate-50 flex items-center justify-end shrink-0">
              <button
                type="button"
                onClick={closeViewDrawer}
                className="px-5 py-2.5 text-white text-sm bg-primary font-bold rounded-lg transition-colors hover:bg-secondary"
              >
                Close View
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* ─────────────────────────────────────────────────────────────────── */}
      {/* RIGHT-SIDE FORM DRAWER (Off-canvas) */}
      {/* ─────────────────────────────────────────────────────────────────── */}
      <div
        className={`fixed inset-0 top-[57px] md:top-[65px] z-[65]  overflow-hidden transition-all duration-300 ${
          isFormOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
      >
        <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" onClick={closeFormDrawer} />
        
        <div className="absolute inset-y-0 right-0 max-w-full flex">
          <div
            className={`w-screen max-w-xl bg-white shadow-2xl flex flex-col transform transition-transform duration-300 ease-in-out ${
              isFormOpen ? "translate-x-0" : "translate-x-full"
            }`}
          >
            {/* Drawer Header */}
            <div className="px-6 py-5 border-b border-gray-100 flex items-center justify-between bg-slate-50">
              <div>
                <h3 className="text-lg font-bold text-gray-900">
                  {selectedLeaveType ? "Edit Leave Configuration" : "Create Leave Type"}
                </h3>
                <p className="text-xs font-bold text-gray-400 mt-0.5">Specify rules, validation policies, and parameters.</p>
              </div>
              <button
                type="button"
                onClick={closeFormDrawer}
                className="p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-all"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Drawer Scrollable Body Form */}
            <form onSubmit={handleFormSubmit} className="flex-1 overflow-y-auto px-6 py-6 space-y-6">
              {/* SECTION: Basic Identity */}
              <div>
                <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">General Identity</h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-bold text-gray-700 mb-1.5 uppercase">Name *</label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder="e.g., Casual Leave"
                      className={`w-full h-10 px-3 border rounded-md text-sm focus:outline-none transition-all ${
                        formErrors.name ? "border-red-500 focus:ring-1 focus:ring-red-200" : "border-gray-200 focus:ring-1 focus:ring-blue-100"
                      }`}
                    />
                    {formErrors.name && <p className="text-xs text-red-500 mt-1 font-medium">{formErrors.name}</p>}
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-gray-700 mb-1.5 uppercase">Short Code *</label>
                    <input
                      type="text"
                      name="code"
                      value={formData.code}
                      onChange={handleInputChange}
                      placeholder="e.g., CL"
                      className={`w-full h-10 px-3 border rounded-md text-sm uppercase focus:outline-none transition-all ${
                        formErrors.code ? "border-red-500 focus:ring-1 focus:ring-red-200" : "border-gray-200 focus:ring-1 focus:ring-blue-100"
                      }`}
                    />
                    {formErrors.code && <p className="text-xs text-red-500 mt-1 font-medium">{formErrors.code}</p>}
                  </div>
                </div>
              </div>

              {/* SECTION: Dropdown & Native Color Picker */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 border-t border-gray-100 pt-5">
                <div>
                  <SelectDropdown
                    label="Applicable Gender"
                    options={genderOptions}
                    value={formData.applicableGender}
                    onChange={(val) => handleSelectChange("applicableGender", val)}
                    labelKey="label"
                    valueKey="id"
                    zIndex={66}
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-gray-700 mb-1.5 uppercase">Badge Color Theme</label>
                  <div className="flex items-center gap-3">
                    <input
                      type="color"
                      name="color"
                      value={formData.color}
                      onChange={handleInputChange}
                      className="w-10 h-10 border border-gray-200 rounded-lg cursor-pointer p-0.5 bg-white shrink-0 transition-all hover:scale-105"
                    />
                    <input
                      type="text"
                      name="color"
                      value={formData.color}
                      onChange={handleInputChange}
                      placeholder="#6366f1"
                      className="w-full h-10 px-3 border border-gray-200 rounded-md text-sm uppercase focus:outline-none focus:ring-1 focus:ring-blue-100"
                    />
                  </div>
                </div>
              </div>

              {/* SECTION: Days Configuration */}
              <div className="border-t border-gray-100 pt-5 space-y-4">
                <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider">Allocation Parameters</h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-bold text-gray-700 mb-1.5 uppercase">Max Days Per Year</label>
                    <input
                      type="number"
                      name="maxDaysPerYear"
                      value={formData.maxDaysPerYear}
                      onChange={handleInputChange}
                      className="w-full h-10 px-3 border border-gray-200 rounded-md text-sm focus:outline-none"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-gray-700 mb-1.5 uppercase">Max Consecutive Days Allowed</label>
                    <input
                      type="number"
                      name="maxConsecutiveDays"
                      value={formData.maxConsecutiveDays}
                      onChange={handleInputChange}
                      placeholder="0 = No limit"
                      className="w-full h-10 px-3 border border-gray-200 rounded-md text-sm focus:outline-none"
                    />
                  </div>
                </div>
              </div>

              {/* SECTION: Carry Forward Rules */}
              <div className="border-t border-gray-100 pt-5 space-y-4">
                <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider">Carry Forward Rules</h4>
                <div className="bg-slate-50/50 p-4 rounded-xl border border-gray-100 space-y-4">
                  <label className="flex items-center justify-between cursor-pointer">
                    <div>
                      <span className="text-sm font-semibold text-gray-800">Enable Carry Forward</span>
                      <p className="text-[11px] text-gray-400">Allows unused leaves to carry over to the next year.</p>
                    </div>
                    <input
                      type="checkbox"
                      name="carryForward"
                      checked={formData.carryForward}
                      onChange={handleInputChange}
                      className="h-4 w-4 accent-red-600 cursor-pointer"
                    />
                  </label>

                  {formData.carryForward && (
                    <div className="pt-3 border-t border-gray-200 animate-fadeIn">
                      <label className="block text-xs font-bold text-gray-700 mb-1.5 uppercase">Max Carry Forward Days</label>
                      <input
                        type="number"
                        name="maxCarryForward"
                        value={formData.maxCarryForward}
                        onChange={handleInputChange}
                        className="w-full h-10 px-3 border border-gray-200 rounded-md text-sm focus:outline-none"
                      />
                      {formErrors.maxCarryForward && <p className="text-xs text-red-500 mt-1 font-medium">{formErrors.maxCarryForward}</p>}
                    </div>
                  )}
                </div>
              </div>

              {/* SECTION: Advanced Toggles (Boolean rules) */}
              <div className="border-t border-gray-100 pt-5 space-y-4">
                <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider">Advanced Guidelines</h4>
                
                <div className="grid grid-cols-1 gap-4 bg-slate-50/50 p-4 rounded-xl border border-gray-100">
                  <label className="flex items-center justify-between cursor-pointer">
                    <div>
                      <span className="text-sm font-semibold text-gray-800">Is Paid Leave</span>
                      <p className="text-[11px] text-gray-400">Determines if salary deduction is applicable for this type.</p>
                    </div>
                    <input
                      type="checkbox"
                      name="isPaid"
                      checked={formData.isPaid}
                      onChange={handleInputChange}
                      className="h-4 w-4 accent-red-600 cursor-pointer"
                    />
                  </label>

                  <label className="flex items-center justify-between cursor-pointer">
                    <div>
                      <span className="text-sm font-semibold text-gray-800">Allow Half-day Bookings</span>
                      <p className="text-[11px] text-gray-400">Permit employees to apply for morning/evening sessions.</p>
                    </div>
                    <input
                      type="checkbox"
                      name="allowHalfDay"
                      checked={formData.allowHalfDay}
                      onChange={handleInputChange}
                      className="h-4 w-4 accent-red-600 cursor-pointer"
                    />
                  </label>

                  <label className="flex items-center justify-between cursor-pointer">
                    <div>
                      <span className="text-sm font-semibold text-gray-800">Requires Manager Approval</span>
                      <p className="text-[11px] text-gray-400">If unchecked, the leave request will auto-approve immediately.</p>
                    </div>
                    <input
                      type="checkbox"
                      name="requiresApproval"
                      checked={formData.requiresApproval}
                      onChange={handleInputChange}
                      className="h-4 w-4 accent-red-600 cursor-pointer"
                    />
                  </label>

                  <label className="flex items-center justify-between cursor-pointer">
                    <div>
                      <span className="text-sm font-semibold text-gray-800">Available During Probation</span>
                      <p className="text-[11px] text-gray-400">Permit employees to apply for this while on probation.</p>
                    </div>
                    <input
                      type="checkbox"
                      name="availableDuringProbation"
                      checked={formData.availableDuringProbation}
                      onChange={handleInputChange}
                      className="h-4 w-4 accent-red-600 cursor-pointer"
                    />
                  </label>

                  <label className="flex items-center justify-between cursor-pointer border-t border-gray-100 pt-3 mt-1">
                    <div>
                      <span className="text-sm font-semibold text-gray-800">Active Status</span>
                      <p className="text-[11px] text-gray-400">If unchecked, employees cannot apply for this leave.</p>
                    </div>
                    <input
                      type="checkbox"
                      name="isActive"
                      checked={formData.isActive}
                      onChange={handleInputChange}
                      className="h-4 w-4 accent-red-600 cursor-pointer"
                    />
                  </label>
                </div>
              </div>

              {/* SECTION: Notice Period & Document Upload rule */}
              <div className="border-t border-gray-100 pt-5 space-y-4">
                <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider">Notice & Attachments</h4>
                
                <div>
                  <label className="block text-xs font-bold text-gray-700 mb-1.5 uppercase">Advance Notice Required (Days)</label>
                  <input
                    type="number"
                    name="minNoticeDays"
                    value={formData.minNoticeDays}
                    onChange={handleInputChange}
                    placeholder="0 = Can apply same day"
                    className="w-full h-10 px-3 border border-gray-200 rounded-md text-sm focus:outline-none"
                  />
                </div>

                <div className="bg-slate-50/50 p-4 rounded-xl border border-gray-100 space-y-4">
                  <label className="flex items-center justify-between cursor-pointer">
                    <div>
                      <span className="text-sm font-semibold text-gray-800">Requires Document Upload</span>
                      <p className="text-[11px] text-gray-400">Forces attachment upload (e.g. medical certificates for sick leaves).</p>
                    </div>
                    <input
                      type="checkbox"
                      name="requiresAttachment"
                      checked={formData.requiresAttachment}
                      onChange={handleInputChange}
                      className="h-4 w-4 accent-red-600 cursor-pointer"
                    />
                  </label>

                  {formData.requiresAttachment && (
                    <div className="pt-3 border-t border-gray-200 animate-fadeIn">
                      <label className="block text-xs font-bold text-gray-700 mb-1.5 uppercase">Attachment Threshold (Days)</label>
                      <input
                        type="number"
                        name="attachmentThresholdDays"
                        value={formData.attachmentThresholdDays}
                        onChange={handleInputChange}
                        className="w-full h-10 px-3 border border-gray-200 rounded-md text-sm focus:outline-none"
                      />
                      {formErrors.attachmentThresholdDays && <p className="text-xs text-red-500 mt-1 font-medium">{formErrors.attachmentThresholdDays}</p>}
                    </div>
                  )}
                </div>
              </div>
            </form>

            {/* Drawer Footer Buttons */}
            <div className="px-6 py-4 border-t border-gray-100 bg-slate-50 flex items-center justify-end gap-3 shrink-0">
              <button
                type="button"
                onClick={closeFormDrawer}
                className="px-5 py-2.5 border border-gray-200 rounded-lg text-sm font-semibold text-gray-600 hover:bg-gray-100 transition-colors"
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                onClick={handleFormSubmit}
                disabled={isSubmitting}
                className="px-5 py-2.5 rounded-lg text-sm font-bold text-white bg-primary hover:bg-secondary transition-colors disabled:opacity-50"
              >
                {isSubmitting ? "Saving Parameters..." : selectedLeaveType ? "Update Type" : "Create Type"}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* ─────────────────────────────────────────────────────────────────── */}
      {/* DELETE CONFIRMATION OFF-CANVAS (SLIDEOUT TOP-BAR MODAL STYLE) */}
      {/* ─────────────────────────────────────────────────────────────────── */}
      <div
        className={`fixed inset-0 top-[57px] md:top-[65px] z-[65] bg-black/30 backdrop-blur-sm transition-opacity duration-300 ${
          isDeleteOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
        onClick={() => setIsDeleteOpen(false)}
      >
        <div
          className={`absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-2xl bg-white shadow-2xl rounded-b-2xl transform transition-transform duration-300 pb-6 px-6 pt-5 ${
            isDeleteOpen ? "translate-y-0" : "-translate-y-full"
          }`}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 flex items-center justify-center rounded-xl bg-red-50 text-red-600 border border-red-100">
                <ShieldAlert className="h-5 w-5" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-gray-900">Delete Leave Type</h2>
                <p className="text-xs text-gray-400">Please confirm if you want to deactivate this leave configuration.</p>
              </div>
            </div>
            <button
              type="button"
              className="flex h-8 w-8 items-center justify-center rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-50"
              onClick={() => setIsDeleteOpen(false)}
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Body Content */}
          <div className="mt-5">
            {!deleteError ? (
              <div>
                <p className="text-gray-600 text-sm leading-relaxed">
                  Are you sure you want to deactivate{" "}
                  <span className="font-bold text-gray-800">{selectedLeaveType?.name}</span>? 
                  This will prevent new policies from linking this leave type. Existing employee balances using this configuration will remain active.
                </p>
                
                {/* Buttons Action Wrapper */}
                <div className="mt-6 flex justify-end gap-3">
                  <button
                    type="button"
                    className="px-4 py-2 border border-gray-200 rounded-lg text-xs font-semibold text-gray-600 hover:bg-gray-50 transition-all"
                    onClick={() => setIsDeleteOpen(false)}
                    disabled={isLoading}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="px-4 py-2 rounded-lg text-xs font-bold text-white bg-primary hover:bg-secondary transition-all hover:opacity-90"
                    onClick={confirmDelete}
                    disabled={isLoading}
                  >
                    {isLoading ? "Archiving..." : "Archive Configuration"}
                  </button>
                </div>
              </div>
            ) : (
              <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-r-xl">
                <p className="text-xs font-semibold text-red-800">{deleteError.message}</p>
                <div className="mt-4 flex justify-end">
                  <button
                    type="button"
                    onClick={() => setIsDeleteOpen(false)}
                    className="bg-white text-gray-500 border border-gray-200 px-4 py-2 rounded-lg text-xs font-bold hover:bg-gray-50"
                  >
                    Dismiss
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LeaveTypesPage;